import { useCallback, useState } from "react";
import { toast } from "react-toastify";

export interface UseCopyToClipboardOptions {
  errorMessage?: string;
  showToast?: boolean;
  successMessage?: string;
}

export interface UseCopyToClipboardReturn {
  copied: boolean;
  copyToClipboard: (text: string) => Promise<void>;
}

/**
 * Custom hook for copying text to clipboard with optional toast notifications
 * @param options - Configuration options for success and error messages
 * @returns Object with copied state and copy function
 */
export const useCopyToClipboard = (
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardReturn => {
  const [copied, setCopied] = useState(false);
  const {
    errorMessage = "Failed to copy to clipboard",
    showToast = false,
    successMessage = "Copied to clipboard",
  } = options;

  const copyToClipboard = useCallback(
    async (text: string): Promise<void> => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        if (showToast) {
          toast.success(successMessage);
        }
      } catch (err) {
        console.error("Failed to copy text:", err);
        if (showToast) {
          toast.error(errorMessage);
        }
      }
    },
    [successMessage, errorMessage, showToast],
  );

  return {
    copied,
    copyToClipboard,
  };
};
